import { useMemo } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tab, Tabs, Typography } from '@mui/material';

import { AdminSettingsList } from '@app/user/components/AdminSettingsList';

import { RuleFamily } from '../types';
import { FAMILY_SLUGS } from '../constants';
import { FamilyRulesPanel } from './FamilyRulesPanel';
import { RuleEditor } from './RuleEditor';

const TAB_PATHS = [
  '/admin/settings',
  `/admin/settings/${FAMILY_SLUGS[RuleFamily.watchlist]}`,
  `/admin/settings/${FAMILY_SLUGS[RuleFamily.colorCoding]}`,
  `/admin/settings/${FAMILY_SLUGS[RuleFamily.matching]}`,
];

// Matching gained its own tab (03-matching PLAN P2-2, SAA-172/SAA-193) — reversing the earlier
// SAA-150 "no feature -> no tab" decision (S-3) now that the family has an engine and a rules
// spine of its own.
export const SettingsScreen = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();

  // Derived from the URL, not local state, so a deep link or reload lands on the right tab.
  const activeTab = useMemo(() => {
    const index = TAB_PATHS.findIndex((path, i) =>
      i === 0 ? location.pathname === path : location.pathname.startsWith(path),
    );

    return index === -1 ? 0 : index;
  }, [location.pathname]);

  return (
    <>
      {/* The page title lives here, ABOVE the tabs, never inside a per-tab panel
      be `AdminSettingsList`'s own h1, which rendered between the tab bar and its card and broke the
      flush join). Shared across all three tabs, same as `AdminApplicationItem.tsx`'s breadcrumb+h1
      row sitting above its own Tabs. */}
      <Typography variant="h1" sx={{ mb: 3 }}>
        {t('user.page.adminSettings.title')}
      </Typography>

      {/* Copied verbatim from `AdminApplicationItem.tsx` — same four
      MUI fixes, same reasons: MUI's scroller is `overflow: hidden` and clips the `-1px` root overlap
      if it sits on the Tab instead; a bar-level bgcolor would paint every tab, so idle vs selected
      live on `.MuiTab-root` / `&.Mui-selected`; `borderRightWidth`, not `borderRight` (the shorthand
      resets the colour to `currentColor`); `&.Mui-selected` repaints label AND icon. */}
      <Tabs
        value={activeTab}
        onChange={(_event, value: number) => navigate(TAB_PATHS[value])}
        aria-label={t('user.page.adminSettings.title') ?? undefined}
        sx={{
          minHeight: '52px',
          width: 'fit-content',
          position: 'relative',
          zIndex: 1,
          mb: '-1px',
          '& .MuiTabs-scroller': { overflow: 'visible !important' },
          '& .MuiTabs-indicator': { display: 'none' },
          '& .MuiTab-root': {
            minHeight: '52px',
            textTransform: 'none',
            color: 'text.primary',
            bgcolor: 'primary.light',
            border: '1px solid',
            borderColor: 'grey.500',
            borderRadius: 0,
            borderRightWidth: 0,
            '&:first-of-type': { borderTopLeftRadius: '16px' },
            '&:last-of-type': { borderTopRightRadius: '16px', borderRightWidth: '1px' },
            padding: '16px 24px',
            gap: 1,
            '&.Mui-selected': {
              color: 'text.primary',
              fontWeight: 600,
              bgcolor: 'background.paper',
              borderBottom: 'none',
            },
          },
        }}
      >
        <Tab id="settings-tab-0" aria-controls="settings-tabpanel-0" label={t('rules.tab.users')} />
        <Tab id="settings-tab-1" aria-controls="settings-tabpanel-1" label={t('rules.tab.watchlist')} />
        <Tab id="settings-tab-2" aria-controls="settings-tabpanel-2" label={t('rules.tab.colorCoding')} />
        <Tab id="settings-tab-3" aria-controls="settings-tabpanel-3" label={t('rules.tab.matching')} />
      </Tabs>

      {/* Unstyled — the flush "attached" look comes from each route's own top-level Paper
      (`borderRadius: '0 16px 16px 16px'`) sitting directly under the Tabs, not from this wrapper.
      The two panel routes sit at the same tree position, so without a `key` React reuses ONE
      `FamilyRulesPanel` instance across a tab switch and its open dialogs leak to the other family. */}
      <div role="tabpanel" id={`settings-tabpanel-${activeTab}`} aria-labelledby={`settings-tab-${activeTab}`}>
        <Routes>
          <Route index element={<AdminSettingsList />} />
          <Route
            path={`${FAMILY_SLUGS[RuleFamily.watchlist]}`}
            element={<FamilyRulesPanel key={RuleFamily.watchlist} family={RuleFamily.watchlist} />}
          />
          <Route
            path={`${FAMILY_SLUGS[RuleFamily.colorCoding]}`}
            element={<FamilyRulesPanel key={RuleFamily.colorCoding} family={RuleFamily.colorCoding} />}
          />
          <Route
            path={`${FAMILY_SLUGS[RuleFamily.matching]}`}
            element={<FamilyRulesPanel key={RuleFamily.matching} family={RuleFamily.matching} />}
          />
          <Route path=":family/rule/:ruleId" element={<RuleEditor />} />
          <Route path="*" element={<Navigate to="/admin/settings" replace />} />
        </Routes>
      </div>
    </>
  );
};
